import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Text, Button } from '@fragments';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import * as Mui from "@mui/material";
import './styles.scss';
import { CheckCircleRounded, CommitRounded, RotateRightRounded, SendRounded } from '@mui/icons-material';

export default function Accordion({
    title,
    summary,
    stepTitle,
    stepDescription,
    isOpen,
    isCompleted,
    onToggle,
    onNext,
    disabled,
    loading,
    children
}) {
    const [error, setError] = useState(null);
    const accordionRef = useRef(null);

    useEffect(() => {
        if (isOpen && accordionRef.current) {
            setTimeout(() => {
                if (accordionRef.current) {
                    accordionRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 500);
        }
    }, [isOpen]);

    const handleNext = () => {
        if (!onNext) return;
        const validationError = onNext();
        if (validationError) {
            setError(validationError);
        } else {
            setError(null);
        }
    };

    const handleToggle = () => {
        if (disabled || loading) return;
        onToggle();
    };

    return (
        <div 
            ref={accordionRef}
            className={`custom-accordion ${isOpen ? 'open' : ''} ${isCompleted ? 'completed' : ''} ${disabled || loading ? 'disabled' : ''}`}
        >
            <div className="accordion-header" onClick={handleToggle}>
                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Text.h4
                        left={
                            isCompleted ? (
                                <CheckCircleRounded className="status-icon completed" />
                            ) : (
                                <CommitRounded className="status-icon pending" />
                            )
                        }
                        right={
                            loading ? (
                                <Mui.CircularProgress size={14} thickness={5} sx={{ color: 'rgba(255,255,255,0.4)' }} />
                            ) : (
                                <div className={`status-label ${isCompleted ? 'completed' : 'pending'}`}>
                                    <Text.small weight={400}>{isCompleted ? 'Completo' : 'Pendente'}</Text.small>
                                </div>
                            )
                        }
                        weight={500}
                        contrast={isOpen ? 9 : 7}
                        className="step-title">
                        {title}
                    </Text.h4>
                    {isCompleted && summary && (
                        <Text.h6 contrast={5} weight={300} style={{ marginLeft: 27 }}>
                            {summary}
                        </Text.h6>
                    )}
                </div>

                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className='arrow-side-container'
                    style={{ transformOrigin: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <KeyboardArrowDownRoundedIcon className="arrow-icon" />
                </motion.div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="accordion-content-wrapper"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div className="accordion-content">
                            <div className="content-header">
                                <Text.h4 weight={400}>{stepTitle}</Text.h4>
                                <Text.h5 contrast={5} weight={300} className="mt-1">{stepDescription}</Text.h5>
                            </div>

                            <div className="content-body">
                                {children}
                            </div>

                            {error && (
                                <Mui.Alert severity="error" className="mt-3">
                                    {error}
                                </Mui.Alert>
                            )}

                            {onNext && (
                                <div className="content-bottom mt-4">
                                    <Button
                                        color="inverse"
                                        onClick={handleNext}
                                        variant="primary"
                                        endIcon={<SendRounded />}
                                    >
                                        Concluir
                                    </Button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
